import React, { Component } from "react";
import ReCaptcha from "react-google-recaptcha";
import * as CaptchaType from "../../api/gen/core/CaptchaType";

const ALTCHA_STRINGS_CS = JSON.stringify({
    ariaLinkLabel: "Navštivit Altcha",
    error: "Ověření selhalo.",
    expired: "Ověření vypršelo.",
    footer: "",
    label: "Nejsem robot",
    verified: "Ověřeno",
    verifying: "Ověřuji...",
    waitAlert: "Ověřuji...",
});

const ALTCHA_STRINGS_EN = JSON.stringify({
    ariaLinkLabel: "Visit Altcha",
    error: "Verification failed.",
    expired: "Verification expired.",
    footer: "",
    label: "I'm not a robot",
    verified: "Verified",
    verifying: "Verifying...",
    waitAlert: "Verifying...",
});

const resolveLang = (lang?: string): "cs" | "en" => (lang === "en" ? "en" : "cs");

// Cache pro načtené skripty z CDN
const loadedScripts: Record<string, Promise<void>> = {};

function loadScript(src: string, isModule = false): Promise<void> {
    if (loadedScripts[src]) {
        return loadedScripts[src];
    }
    loadedScripts[src] = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        if (isModule) {
            script.type = "module";
        } else {
            script.async = true;
        }
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Nepodařilo se načíst skript: ${src}`));
        document.head.appendChild(script);
    });
    return loadedScripts[src];
}

interface CaptchaConfig {
    type: string;
    siteKey?: string;
    endpoint?: string;
    challengeUrl?: string;
    sdkUrl?: string;
}

interface CaptchaWidgetProps {
    captchaConfig: CaptchaConfig;
    value?: string;
    onChange?: (value: string | null) => void;
    lang?: string;
}

interface CaptchaWidgetState {
    ready: boolean;
}

/**
 * Univerzální CAPTCHA komponenta – renderuje správný widget dle typu (RECAPTCHA, FRIENDLYCAPTCHA, ALTCHA).
 * Kompatibilní s ff.Base / redux-form přes onChange callback.
 */
class CaptchaWidget extends Component<CaptchaWidgetProps, CaptchaWidgetState> {
    private altchaRef = React.createRef<HTMLElement>();
    private friendlyCaptchaContainerRef = React.createRef<HTMLDivElement>();

    state: CaptchaWidgetState = {
        ready: false,
    };

    componentDidMount() {
        const { captchaConfig } = this.props;

        if (captchaConfig.type === CaptchaType.FRIENDLYCAPTCHA) {
            // Div musí být v DOMu dříve než se načte SDK, aby ho SDK auto-inicializovalo
            this.setState({ ready: true }, () => {
                loadScript(captchaConfig.sdkUrl)
                    .then(() => {
                        this.initFriendlyCaptcha();
                    })
                    .catch((err) => console.error("Chyba načtení FriendlyCaptcha SDK:", err));
            });
        } else if (captchaConfig.type === CaptchaType.ALTCHA) {
            // Altcha je ES modul – načítá se přes type="module", custom element se zaregistruje po načtení
            loadScript(captchaConfig.sdkUrl!, true)
                .then(() => {
                    this.setState({ ready: true });
                })
                .catch((err) => console.error("Chyba načtení Altcha widgetu:", err));
        } else {
            this.setState({ ready: true });
        }
    }

    componentDidUpdate(prevProps: CaptchaWidgetProps, prevState: CaptchaWidgetState) {
        const { captchaConfig } = this.props;
        const langChanged = prevProps.lang !== this.props.lang;

        if (captchaConfig.type === CaptchaType.ALTCHA) {
            // Připojit listener při přechodu do ready, nebo po změně jazyka (key způsobí remount elementu)
            if ((!prevState.ready && this.state.ready) || (this.state.ready && langChanged)) {
                this.altchaRef.current?.addEventListener("statechange", this.handleAltchaStateChange);
            }
        }

        if (captchaConfig.type === CaptchaType.FRIENDLYCAPTCHA) {
            if (this.state.ready && langChanged) {
                // Krok 1: skryjeme div – SDK 0.2.x neprovádí MutationObserver reinit, proto je nutný čistý restart
                this.setState({ ready: false });
            } else if (prevState.ready && !this.state.ready) {
                // Krok 2: div je mimo DOM – přidáme ho zpět a použijeme existující window.frcaptcha.attach()
                // (nový div nemá frcWidget, attach() ho najde a inicializuje bez konfliktu s bus infrastrukturou)
                this.setState({ ready: true }, () => {
                    (window as any).frcaptcha?.attach();
                    this.initFriendlyCaptcha();
                });
            }
        }
    }

    componentWillUnmount() {
        if (this.altchaRef.current) {
            this.altchaRef.current.removeEventListener("statechange", this.handleAltchaStateChange);
        }
        if (this.friendlyCaptchaContainerRef.current) {
            const container = this.friendlyCaptchaContainerRef.current;
            container.removeEventListener("frc:widget.statechange", this.handleFriendlyCaptchaVerified as EventListener);
        }
    }

    initFriendlyCaptcha = () => {
        const container = this.friendlyCaptchaContainerRef.current;
        if (!container) return;
        container.addEventListener("frc:widget.statechange", this.handleFriendlyCaptchaVerified as EventListener);
    }

    handleFriendlyCaptchaVerified = (e: CustomEvent) => {
        if (e.detail?.state === "completed") {
            this.props.onChange?.(e.detail.response);
        } else if (e.detail && (e.detail.state === "expired" || e.detail.state === "error")) {
            this.props.onChange?.(null);
        }
    };

    handleAltchaStateChange = (e: Event) => {
        const event = e as CustomEvent;
        if (event.detail?.state === "verified") {
            // payload je base64 JSON string – primárně z value elementu, fallback z detail.payload
            const payload = (e.target as any)?.value || event.detail?.payload || null;
            this.props.onChange?.(payload);
        } else if (event.detail?.state === "expired" || event.detail?.state === "error") {
            this.props.onChange?.(null);
        }
    };

    render() {
        const { captchaConfig, onChange } = this.props;
        const { ready } = this.state;

        if (!ready) {
            return null;
        }

        if (captchaConfig.type === CaptchaType.RECAPTCHA) {
            return (
                <ReCaptcha
                    sitekey={captchaConfig.siteKey}
                    onChange={onChange}
                />
            );
        }

        if (captchaConfig.type === CaptchaType.FRIENDLYCAPTCHA) {
            const resolvedLang = resolveLang(this.props.lang);
            return (
                <div
                    ref={this.friendlyCaptchaContainerRef}
                    className="frc-captcha"
                    data-sitekey={captchaConfig.siteKey}
                    data-endpoint={captchaConfig.endpoint}
                    data-lang={resolvedLang}
                />
            );
        }

        if (captchaConfig.type === CaptchaType.ALTCHA) {
            const resolvedLang = resolveLang(this.props.lang);
            const altchaStrings = resolvedLang === "en" ? ALTCHA_STRINGS_EN : ALTCHA_STRINGS_CS;
            return (
                <altcha-widget
                    key={resolvedLang}
                    ref={this.altchaRef}
                    auto="onload"
                    challengeurl={captchaConfig.challengeUrl}
                    strings={altchaStrings}
                />
            );
        }

        return null;
    }
}

export default CaptchaWidget;
